import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestJson, fetchCurrentUser } from '../utils/authClient.js';
import { localizePath } from '../utils/localizedRoutes.js';
import { AdminPageHead, AdminSection } from '../components/AdminNav.jsx';
import { formatBerlin } from './VeridionShared.jsx';

// The software development of the company (IDD/projects/veridion/gitlab): coding tasks, GitLab sync and the
// live previews of the projects in development. The model-architecture views moved to the sovereign LLM
// paper page (`LlmPaperPage.jsx`, IDD D-HP-ADMIN-062); the demos view here shows the previews alone.
const CORE = '/api/admin/veridion-core';
const REFRESH_MS = 10000;
const OPEN = ['queued', 'working', 'waiting', 'review', 'merged', 'needs_person', 'paused'];

const call = (path, body) => requestJson(path, body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) });
const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());
const formatTime = formatBerlin;
const short = (value, length = 160) => {
  const text = typeof value === 'string' ? value : JSON.stringify(value ?? '');
  return text.length > length ? `${text.slice(0, length)}…` : text;
};

const Section = ({ id = '', title, subtitle, children, actions = null, count = null, collapsed = false, state = '', tone = '' }) => (
  <AdminSection id={id} title={title} subtitle={subtitle} actions={actions} count={count} collapsed={collapsed} state={state} tone={tone}>{children}</AdminSection>
);

const Notice = ({ state }) => (state?.message ? (
  <div className={`homepage-muted-panel p-3 text-sm ${state.error ? 'text-error' : 'text-text-secondary'}`}>{state.message}</div>
) : null);

const useAction = (s, onDone) => {
  const [state, setState] = useState({ busy: false, message: '', error: false });
  const run = async (action, doneMessage) => {
    setState({ busy: true, message: '', error: false });
    try {
      const result = await action();
      setState({ busy: false, message: doneMessage || s.done, error: false });
      if (onDone) onDone(result);
      return result;
    } catch (error) {
      setState({ busy: false, message: `${s.failed} (${error?.code || error?.status || '-'})`, error: true });
      return null;
    }
  };
  return [state, run];
};

const Meter = ({ label, used, total, unit }) => {
  const share = total ? Math.min(100, Math.round((Number(used || 0) / Number(total)) * 100)) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm"><span>{label}</span><span>{formatInteger(used)} / {formatInteger(total)} {unit}</span></div>
      <div style={{ height: '6px', background: 'var(--color-border, #e5e5e5)' }}>
        <div style={{ width: `${share}%`, height: '6px', background: share > 90 ? '#FF0088' : '#111' }} />
      </div>
    </div>
  );
};

const Ext = ({ href, children }) => (href
  ? <a className="underline" href={href} target="_blank" rel="noreferrer">{children}</a>
  : <span>{children}</span>);

const linked = (text) => String(text).split(/(https?:\/\/[^\s)]+)/g).map((part, index) => (
  /^https?:\/\//.test(part) ? <Ext key={index} href={part}>{part.replace(/^https?:\/\//, '')}</Ext> : part
));

const Reasons = ({ s, reasons, reason }) => {
  const items = Array.isArray(reasons) && reasons.length ? reasons.filter(Boolean) : (reason ? [reason] : []);
  if (!items.length) return null;
  return (
    <details className="admin-reasons" open={items.length <= 3}>
      <summary>{s.reasonsLabel} ({items.length})</summary>
      <ol>{items.map((item, index) => <li key={index}>{linked(item)}</li>)}</ol>
    </details>
  );
};

const ControlButtons = ({ s, preview, run, busy }) => (
  <>
    {preview.canOpen && preview.address ? (
      <a className="homepage-button-primary" href={preview.address} target="_blank" rel="noreferrer"
        onClick={() => { if (preview.id) call(`${CORE}/previews/${preview.id}/touch`, {}).catch(() => null); }}>{s.openPreview}</a>
    ) : null}
    {preview.canStart ? (
      <button type="button" className="homepage-button-secondary" disabled={busy}
        onClick={() => run(() => call(`${CORE}/previews/${preview.id}/start`, {}), s.previewStarted)}>{s.startPreview}</button>
    ) : null}
    {preview.canStop ? (
      <button type="button" className="homepage-button-secondary" disabled={busy}
        onClick={() => run(() => call(`${CORE}/previews/${preview.id}/stop`, {}), s.previewStopped)}>{s.stopPreview}</button>
    ) : null}
    {preview.canRestart ? (
      <button type="button" className="homepage-button-secondary" disabled={busy}
        onClick={() => run(() => call(`${CORE}/previews/${preview.id}/restart`, {}), s.previewRestarted)}>{s.restartPreview}</button>
    ) : null}
  </>
);

const ResultBlock = ({ s, result }) => {
  const mergeRequest = result.mergeRequest || {};
  const pipeline = result.pipeline || {};
  const rows = [
    result.project ? [s.repository, <Ext href={result.projectUrl}>{result.project}</Ext>] : null,
    result.branch ? [s.branch, <Ext href={result.branchUrl}>{result.branch}</Ext>] : null,
    mergeRequest.url ? [s.mergeRequest, <Ext href={mergeRequest.url}>{mergeRequest.iid ? `!${mergeRequest.iid} ` : ''}{mergeRequest.title || ''}{mergeRequest.state ? ` (${mergeRequest.state})` : ''}</Ext>] : null,
    pipeline.url ? [s.pipeline, <Ext href={pipeline.url}>{pipeline.status || '-'}</Ext>] : null,
    result.sonar?.url ? [s.resultSonar, <Ext href={result.sonar.url}>{result.sonar.key || result.sonar.url}</Ext>] : null,
  ].filter(Boolean);
  const jobs = Array.isArray(pipeline.jobs) ? pipeline.jobs : [];
  const delivered = Array.isArray(result.delivered) ? result.delivered : [];
  if (!rows.length && !jobs.length && !delivered.length) return null;
  return (
    <div className="site-card p-3 text-sm space-y-1">
      <div className="font-black">{s.resultTitle}</div>
      {rows.map(([label, value]) => <div key={label}>{label}: {value}</div>)}
      {jobs.length ? (
        <div>{s.resultJobs}: {jobs.map((job) => (
          <span key={job.id || job.name} className="mr-3">
            <Ext href={job.url}>{job.name}</Ext>{job.status ? ` (${job.status})` : ''}
            {(job.artifacts || []).map((artifact) => <span key={artifact.url} className="ml-1">· <Ext href={artifact.url}>{artifact.name || artifact.url}</Ext></span>)}
          </span>
        ))}</div>
      ) : null}
      {delivered.length ? (
        <div>{s.resultDelivered}: {delivered.map((link) => <span key={link.url} className="mr-3"><Ext href={link.url}>{link.title || link.url}</Ext></span>)}</div>
      ) : null}
    </div>
  );
};

const TaskDetail = ({ s, taskId, gitlabUrl, onChanged, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [text, setText] = useState('');
  const [urgent, setUrgent] = useState(false);
  const load = useCallback(async () => {
    try {
      setDetail(await call(`${CORE}/tasks/${taskId}`));
    } catch (error) {
      setDetail({ error: error?.code || 'unavailable' });
    }
  }, [taskId]);
  const [state, run] = useAction(s, () => { load(); onChanged(); });
  useEffect(() => { load(); const timer = setInterval(load, REFRESH_MS); return () => clearInterval(timer); }, [load]);

  if (!detail) return <div className="homepage-muted-panel p-4 text-sm">{s.loading}</div>;
  if (detail.error) return <div className="homepage-muted-panel p-4 text-sm text-error">{s.failed} ({detail.error})</div>;
  const task = detail.task;
  const open = OPEN.includes(task.status);

  return (
    <div className="homepage-muted-panel p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-xl font-black">{task.title} <span className="text-sm font-normal text-text-secondary">({task.id})</span></h3>
          <div className="text-sm text-text-secondary">
            {s.statusLabel}: {s.status?.[task.status] || task.status}{task.paused_reason ? ` (${task.paused_reason})` : ''} · {s.requester}: {task.requester || task.origin}
            {task.issue ? ` · YouTrack ${task.issue}` : ''}
          </div>
          <div className="text-sm text-text-secondary">
            {s.repository}: {task.repository || '-'} · {s.branch}: {gitlabUrl && task.branch && task.repository && task.repository.includes('/')
              ? <a className="underline" href={`${gitlabUrl}/${task.repository}/-/tree/${task.branch}`} target="_blank" rel="noreferrer">{task.branch}</a>
              : (task.branch || '-')} · {s.tokens}: {formatInteger(task.used_tokens)}
          </div>
          <div className="text-sm">
            {task.mr_url ? <a className="underline mr-4" href={task.mr_url} target="_blank" rel="noreferrer">{s.mergeRequest} !{task.mr_iid}{task.mr_state ? ` (${task.mr_state})` : ''}</a> : null}
            {task.pipeline_url ? <a className="underline" href={task.pipeline_url} target="_blank" rel="noreferrer">{s.pipeline}: {task.pipeline_status}</a> : null}
          </div>
        </div>
        <button type="button" className="homepage-button-secondary" onClick={onClose}>{s.close}</button>
      </div>
      {task.phase ? <div className="text-sm"><strong>{s.phase}:</strong> {task.phase}</div> : null}
      {task.question ? <div className="site-card p-3 text-sm"><strong>{s.openQuestion}:</strong> {task.question}</div> : null}
      {task.result ? <div className="site-card p-3 text-sm" style={{ whiteSpace: 'pre-wrap' }}><strong>{s.result}:</strong> {task.result}</div> : null}
      {detail.result ? <ResultBlock s={s} result={detail.result} /> : null}

      {open ? (
        <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); run(() => call(`${CORE}/tasks/${task.id}/messages`, { text, urgent }), s.messageSent).then((ok) => ok && setText('')); }}>
          <label className="block text-sm font-bold">{task.status === 'waiting' ? s.answerQuestion : s.sendMessage}</label>
          <textarea className="form-input w-full" rows={3} value={text} onChange={(event) => setText(event.target.value)} required />
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={urgent} onChange={(event) => setUrgent(event.target.checked)} />{s.urgent}</label>
            <button type="submit" className="homepage-button-primary" disabled={state.busy}>{s.send}</button>
          </div>
        </form>
      ) : null}

      {open ? (
        <div className="flex flex-wrap items-end gap-2">
          {['pause', 'resume', 'cancel'].map((command) => (
            <button key={command} type="button" className="homepage-button-secondary" disabled={state.busy}
              onClick={() => (command === 'cancel' && !window.confirm(s.confirmCancel) ? null : run(() => call(`${CORE}/tasks/${task.id}/command`, { command })))}>
              {s.commands[command]}
            </button>
          ))}
        </div>
      ) : null}
      <Notice state={state} />

      <h4 className="font-black">{s.course}</h4>
      <div className="text-sm space-y-2" style={{ maxHeight: '420px', overflowY: 'auto' }}>
        {detail.course?.length ? detail.course.map((entry, index) => (
          <div key={index} className={entry.role === 'user' ? 'site-card p-2' : 'p-2'} style={{ whiteSpace: 'pre-wrap' }}>
            <div className="text-xs text-text-secondary">{formatTime(entry.time)} · {entry.role}</div>
            {entry.text ? <div>{short(entry.text, 2000)}</div> : null}
            {entry.tools?.length ? <div className="text-xs text-text-secondary" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}>{entry.tools.join(' · ')}</div> : null}
          </div>
        )) : <div className="text-text-secondary">{s.none}</div>}
      </div>
      <h4 className="font-black">{s.messages}</h4>
      <div className="text-xs space-y-1" style={{ maxHeight: '200px', overflowY: 'auto' }}>
        {detail.messages?.length ? detail.messages.map((message) => (
          <div key={message.id}>{formatTime(message.created_at)} · {message.sender || '-'} · {message.kind}{message.urgent ? ' !' : ''} · {message.status}: {short(message.body?.text, 300)}</div>
        )) : <div className="text-text-secondary">{s.none}</div>}
      </div>
      <h4 className="font-black">{s.events}</h4>
      <div className="text-xs space-y-1" style={{ maxHeight: '200px', overflowY: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}>
        {detail.events?.length ? detail.events.map((event, index) => (
          <div key={index}>{formatTime(event.created_at)} · {event.event_type}{event.detail ? ` · ${short(event.detail, 240)}` : ''}</div>
        )) : <div className="text-text-secondary">{s.none}</div>}
      </div>
    </div>
  );
};

// -- Main Page Component ----------------------------------------------------------------------------

const SoftwareDevelopmentPage = ({ embedded = false, view = '' }) => {
  const { t, currentLang } = useTranslation();
  const s = t.admin.software;
  const [allowed, setAllowed] = useState(null);
  const [overview, setOverview] = useState(null);
  const [gitlab, setGitlab] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showFinished, setShowFinished] = useState(false);
  const [openTask, setOpenTask] = useState('');
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({ brief: '', repository: '', issue: '' });

  const load = useCallback(async () => {
    const [overviewResult, gitlabResult, taskResult, previewResult] = await Promise.allSettled([
      call(`${CORE}/gitlab/overview`), call('/api/admin/gitlab/state'),
      call(`${CORE}/tasks?only_open=${showFinished ? 'false' : 'true'}`),
      call(`${CORE}/previews`),
    ]);
    if (overviewResult.status === 'fulfilled') { setOverview(overviewResult.value); setError(''); } else setError(overviewResult.reason?.code || 'unavailable');
    if (gitlabResult.status === 'fulfilled') setGitlab(gitlabResult.value);
    if (taskResult.status === 'fulfilled') setTasks(taskResult.value.items || []);
    if (previewResult.status === 'fulfilled') { setPreviews(previewResult.value.items || []); setProjects(previewResult.value.projects || []); }
  }, [showFinished]);
  const [state, run] = useAction(s || {}, load);

  useEffect(() => {
    fetchCurrentUser().then((current) => setAllowed(Boolean(current.user?.roles?.includes('admin')))).catch(() => setAllowed(false));
  }, []);
  useEffect(() => {
    if (!allowed) return undefined;
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [allowed, load]);

  if (allowed === null) return <div className="site-card p-6">{s.loading}</div>;
  if (!allowed) return <main className="site-main"><div className="site-card p-6">{t.auth.errors.adminRequired}</div></main>;

  const capacity = overview?.capacity || {};
  const node = capacity.node || {};
  const disk = capacity.disk || {};
  const host = capacity.host || {};
  const sync = gitlab?.sync || {};
  const syncedRepositories = sync.repositories && Object.keys(sync.repositories).length
    ? Object.entries(sync.repositories).map(([project, entry]) => ({ project, ...entry }))
    : [{ project: '', ...sync }];
  const housekeeping = gitlab?.housekeeping || {};
  const history = Array.isArray(capacity.history) ? capacity.history : [];
  const gitlabUrl = String(overview?.gitlabUrl || '').replace(/\/+$/, '');
  const repositories = Array.isArray(overview?.repositories) ? overview.repositories : [
    overview?.project ? { path: overview.project, kind: 'company' } : null,
    overview?.agentGroup ? { path: overview.agentGroup, kind: 'agents' } : null,
  ].filter(Boolean);
  const repositoryLabel = (entry) => {
    if (entry.kind === 'company') return s.repositoryChoice.company.replace('{project}', entry.path);
    if (entry.kind === 'agents') return s.repositoryChoice.own.replace('{group}', entry.path);
    return entry.title || entry.path;
  };

  const projectsView = !view || view === 'projects';
  const tasksView = !view || view === 'tasks';
  const gitlabView = !view || view === 'gitlab';

  const body = (
    <>
      {!embedded ? (
        <AdminPageHead title={s.title} subtitle={s.subtitle} actions={(
          <>
            <a className="homepage-button-secondary" href={localizePath('/admin', currentLang)}>{t.nav?.admin}</a>
            {gitlabUrl ? <a className="homepage-button-primary" href={gitlabUrl} target="_blank" rel="noreferrer">{s.openGitlab}</a> : null}
            {overview?.sonarUrl ? <a className="homepage-button-secondary" href={overview.sonarUrl} target="_blank" rel="noreferrer">{s.openSonar}</a> : null}
          </>
        )}>
          {error ? <div className="homepage-muted-panel p-4 text-sm text-error">{s.unavailable} ({error})</div> : null}
        </AdminPageHead>
      ) : (
        <div className="admin-page-actions">
          {gitlabUrl ? <a className="homepage-button-primary" href={gitlabUrl} target="_blank" rel="noreferrer">{s.openGitlab}</a> : null}
          {overview?.sonarUrl ? <a className="homepage-button-secondary" href={overview.sonarUrl} target="_blank" rel="noreferrer">{s.openSonar}</a> : null}
          {error ? <span className="text-sm text-error">{s.unavailable} ({error})</span> : null}
        </div>
      )}

      {projectsView ? (
        <>
          {/* The previews-focused executive summary (IDD D-HP-ADMIN-060, D-HP-ADMIN-062). */}
          <div className="homepage-muted-panel p-4 text-sm space-y-1">
            <div className="font-black">{s.previewsSummaryTitle}</div>
            <p className="text-text-secondary">{s.previewsSummaryBody}</p>
          </div>

          <Section id="software-projects" title={s.previewsTitle} subtitle={s.previewsSubtitle} count={projects.length}>
            <ul className="mb-4 grid gap-2 md:grid-cols-2">
              {projects.map((project) => (
                <li key={project.project} className="admin-card">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-black"><Ext href={project.projectUrl}>{String(project.project).split('/').pop()}</Ext></span>
                  </div>
                  {project.status === 'running' && project.address ? <div className="text-sm break-words"><Ext href={project.address}>{String(project.address).replace(/^https?:\/\//, '')}</Ext></div> : null}
                  {project.status !== 'running' ? <div className="text-sm text-text-secondary">{s.previewStatus?.[project.status] || project.status || s.previewStatus?.none || '…'}</div> : null}
                  {Array.isArray(project.reasons) && project.reasons.length ? <Reasons s={s} reasons={project.reasons} reason={project.reason} /> : null}
                </li>
              ))}
              {!projects.length ? <li className="text-text-secondary">{s.noProjects}</li> : null}
            </ul>
            <div className="admin-table-shell"><div className="admin-table-scroll">
              <table className="admin-table">
                <thead><tr><th>{s.previewProject}</th><th>{s.previewState}</th><th>{s.previewSince}</th><th>{s.statusLabel}</th><th>{s.previewAddress}</th><th>{s.previewControl}</th></tr></thead>
                <tbody>
                  {previews.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <Ext href={item.projectUrl}>{item.projectTitle || item.project}</Ext>
                        {item.ref ? <div className="text-xs text-text-secondary">{s.branch}: {item.ref.length === 40 ? item.ref.slice(0, 8) : item.ref}</div> : null}
                      </td>
                      <td>
                        {item.commit?.shortSha ? <Ext href={item.commit.url}>{item.commit.shortSha}</Ext> : '-'}
                        {item.commit?.at ? <div className="text-xs text-text-secondary">{s.previewMergedAt}: {formatTime(item.commit.at)}</div> : null}
                        {item.pipeline?.url ? <div className="text-xs"><Ext href={item.pipeline.url}>{s.pipeline}{item.pipeline.id ? ` #${item.pipeline.id}` : ''}</Ext></div> : null}
                      </td>
                      <td>{formatTime(item.startedAt)}</td>
                      <td className="admin-col-state">
                        {s.previewStatus?.[item.status] || item.status}
                        <Reasons s={s} reasons={item.reasons} reason={item.reason} />
                      </td>
                      <td className="admin-col-address">{item.address ? <Ext href={item.address}>{String(item.address).replace(/^https?:\/\//, '')}</Ext> : '-'}</td>
                      <td className="admin-col-actions"><div className="flex gap-2"><ControlButtons s={s} preview={item} run={run} busy={state.busy} /></div></td>
                    </tr>
                  ))}
                  {!previews.length ? <tr><td colSpan="6">{s.noPreviews}</td></tr> : null}
                </tbody>
              </table>
            </div></div>
            <Notice state={state} />
          </Section>
        </>
      ) : null}

      {tasksView ? (
        <>
          <Section id="software-ready" title={s.readyTitle} subtitle={s.readySubtitle} count={(overview?.readyMergeRequests || []).length}>
            <div className="admin-table-shell"><div className="admin-table-scroll">
              <table className="admin-table">
                <thead><tr><th>{s.mergeRequest}</th><th>{s.branch}</th><th>{s.pipeline}</th><th>{s.updated}</th></tr></thead>
                <tbody>
                  {(overview?.readyMergeRequests || []).map((item) => (
                    <tr key={item.iid}>
                      <td><a className="underline" href={item.url} target="_blank" rel="noreferrer">!{item.iid} {item.title}</a></td>
                      <td className="break-all">{item.branch}</td><td>{item.pipeline || '-'}</td><td>{formatTime(item.updatedAt)}</td>
                    </tr>
                  ))}
                  {!(overview?.readyMergeRequests || []).length ? <tr><td colSpan="4">{s.noReady}</td></tr> : null}
                </tbody>
              </table>
            </div></div>
          </Section>

          <Section id="software-tasks" title={s.tasksTitle} subtitle={s.tasksSubtitle} count={tasks.length}
            actions={<label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={showFinished} onChange={(event) => setShowFinished(event.target.checked)} />{s.showFinished}</label>}>
            <div className="admin-table-shell"><div className="admin-table-scroll">
              <table className="admin-table">
                <thead><tr><th>{s.task}</th><th>{s.statusLabel}</th><th>{s.requester}</th><th>{s.mergeRequest}</th><th>{s.tokens}</th><th>{s.updated}</th></tr></thead>
                <tbody>
                  {tasks.map((task) => (
                    <tr key={task.id} className="cursor-pointer" onClick={() => setOpenTask(task.id)}>
                      <td>{task.title}<div className="text-xs text-text-secondary">{task.id}{task.branch ? ` · ${task.branch}` : ''}</div></td>
                      <td>{s.status?.[task.status] || task.status}{task.question ? ` · ${s.openQuestion}` : ''}</td>
                      <td className="break-all">{task.requester || task.origin}</td>
                      <td>{task.mr_url ? `!${task.mr_iid} ${task.mr_state || ''}` : '-'}</td>
                      <td>{formatInteger(task.used_tokens)}</td>
                      <td>{formatTime(task.updated_at)}</td>
                    </tr>
                  ))}
                  {!tasks.length ? <tr><td colSpan="6">{s.noTasks}</td></tr> : null}
                </tbody>
              </table>
            </div></div>
            {openTask ? <TaskDetail s={s} taskId={openTask} gitlabUrl={gitlabUrl} onChanged={load} onClose={() => setOpenTask('')} /> : null}
          </Section>

          <Section id="software-new" title={s.newTitle} subtitle={s.newSubtitle} collapsed>
            <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); run(() => call(`${CORE}/tasks`, draft), s.created).then((task) => { if (task) { setDraft({ brief: '', repository: '', issue: '' }); setOpenTask(task.id); } }); }}>
              <label className="block text-sm font-bold">{s.brief}</label>
              <textarea className="form-input w-full" rows={4} value={draft.brief} onChange={(event) => setDraft({ ...draft, brief: event.target.value })} required />
              <div className="flex flex-wrap gap-2">
                <select className="form-input" value={draft.repository} onChange={(event) => setDraft({ ...draft, repository: event.target.value })}>
                  <option value="">{s.repositoryChoice.auto}</option>
                  {repositories.map((entry) => <option key={entry.path} value={entry.path}>{repositoryLabel(entry)}</option>)}
                </select>
                <input className="form-input" value={draft.issue} placeholder={s.issue} onChange={(event) => setDraft({ ...draft, issue: event.target.value })} />
                <button type="submit" className="homepage-button-primary" disabled={state.busy}>{s.create}</button>
              </div>
            </form>
            <Notice state={state} />
          </Section>
        </>
      ) : null}

      {gitlabView ? (
        <>
          <Section id="software-sync" title={s.syncTitle} subtitle={s.syncSubtitle}
            actions={<button type="button" className="homepage-button-secondary" disabled={state.busy} onClick={() => run(() => call('/api/admin/gitlab/sync', {}), s.syncStarted)}>{s.syncNow}</button>}>
            <div className="grid gap-4 md:grid-cols-2 text-sm">
              <div className="homepage-muted-panel p-4 space-y-3">
                {syncedRepositories.map((entry) => (
                  <div key={entry.project || 'primary'} className="space-y-1">
                    {entry.project ? <div className="font-bold">{entry.project}{entry.branch ? ` · ${entry.branch}` : ''}</div> : null}
                    <div>{s.stateLabel}: {entry.state || '-'}{entry.error ? ` · ${s.error}: ${entry.error}` : ''}</div>
                    <div>{s.azureCommit}: <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}>{(entry.azureSha || '-').slice(0, 12)}</span></div>
                    <div>{s.gitlabCommit}: <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}>{(entry.gitlabSha || '-').slice(0, 12)}</span></div>
                    <div>{s.lastRun}: {formatTime(entry.at)} · {s.lastChange}: {formatTime(entry.lastChangeAt)}</div>
                    <div>{s.replaced}: {(entry.replaced || []).length ? entry.replaced.slice(-5).map((kept) => `${kept.tag} (${kept.commits})`).join(' · ') : s.noneReplaced}</div>
                  </div>
                ))}
              </div>
              <div className="homepage-muted-panel p-4 space-y-1">
                <div className="font-bold">{s.jobs}</div>
                {(gitlab?.jobs || []).map((job) => <div key={job.name}>{job.name} · {job.state} · {formatTime(job.started)}</div>)}
                {!(gitlab?.jobs || []).length ? <div className="text-text-secondary">{s.none}</div> : null}
              </div>
            </div>
            <Notice state={state} />
          </Section>

          <Section id="software-capacity" title={s.capacityTitle} subtitle={s.capacitySubtitle}>
            <div className="grid gap-4 md:grid-cols-3 text-sm">
              <div className="homepage-muted-panel p-4 space-y-3">
                <div>{s.stateLabel}: <strong>{s.capacityStates?.[capacity.state] || capacity.state || '-'}</strong></div>
                <Meter label={s.memory} used={node.memUsedGib} total={node.memoryGib} unit="GiB" />
                <Meter label={s.disk} used={disk.usedGib} total={disk.sizeGib} unit="GiB" />
                <div>{s.cpu}: {node.cpuUsed ?? '-'} / {node.cpus ?? '-'}</div>
              </div>
              <div className="homepage-muted-panel p-4 space-y-1">
                <div>{s.hostFree}: {formatInteger(host.memAvailableGib)} GiB · {formatInteger(host.diskFreeGib)} GiB</div>
                <div>{s.reserve}: {formatInteger(host.reserve?.memoryGib)} GiB · {formatInteger(host.reserve?.cpus)} CPU · {formatInteger(host.reserve?.diskGib)} GiB</div>
                <div>{s.codingSlots}: {formatInteger(capacity.coding?.slots)}</div>
                <div>{s.pipelineQuota}: {formatInteger(capacity.ci?.memoryGib)} GiB · {formatInteger(capacity.ci?.cpus)} CPU</div>
                <div>{s.lastRun}: {formatTime(capacity.at)}</div>
              </div>
              <div className="homepage-muted-panel p-4 space-y-1 text-xs" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                <div className="font-bold text-sm">{s.recentActions}</div>
                {history.slice().reverse().map((entry, index) => <div key={index}>{formatTime(entry.at)} · {entry.action || s.capacityStates?.[entry.state] || entry.state}</div>)}
                {!history.length ? <div className="text-text-secondary">{s.none}</div> : null}
              </div>
            </div>
          </Section>

          <Section id="software-users" title={s.usersTitle} subtitle={s.usersSubtitle} count={(housekeeping.users || []).length} collapsed>
            <div className="admin-table-shell"><div className="admin-table-scroll">
              <table className="admin-table">
                <thead><tr><th>{s.email}</th><th>{s.gitlabUser}</th><th>{s.stateLabel}</th></tr></thead>
                <tbody>
                  {(housekeeping.users || []).map((user) => (
                    <tr key={`${user.email}-${user.gitlabUser}`}><td className="break-all">{user.email}</td><td>{user.gitlabUser}</td><td>{s.userState?.[user.state] || user.state}</td></tr>
                  ))}
                  {!(housekeeping.users || []).length ? <tr><td colSpan="3">{s.none}</td></tr> : null}
                </tbody>
              </table>
            </div></div>
            <div className="text-sm text-text-secondary">
              {s.lastHousekeeping}: {formatTime(housekeeping.at)} · {s.sonarDeleted}: {(housekeeping.sonarProjectsDeleted || []).length}
              {(housekeeping.errors || []).length ? ` · ${s.error}: ${housekeeping.errors.join(' · ')}` : ''}
            </div>
          </Section>
        </>
      ) : null}
    </>
  );

  if (embedded) return <div className="admin-page-body">{body}</div>;
  return <main className="site-main admin-dashboard-main admin-page-body">{body}</main>;
};

export default SoftwareDevelopmentPage;
